import Link from "next/link";
import { useTranslation } from "react-i18next";
import Color from "../../../../assets/colors";
import { getLastActiveAtTime } from "../../../../helpers/conditional_rendering";
import { currencies } from "../../../../helpers/constant";
import {
  BoxStyled,
  CardStyled,
  CurrencyIconStyled,
  DivStyled,
  IconTextContainer,
  LabelStyled,
  MapPinStyled,
  StatusBadgeContainer,
  StatusClockIcon,
} from "./styles";

function SingleJobHighlight(props) {
  const { job, locationFilter, handleHref } = props;
  const { t } = useTranslation();

  const getJobTypeLabel = (jobTypeValue) => {
    if (!jobTypeValue) return "";
    const key = jobTypeValue
      .toString()
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    return t(`companydetail.job_type_labels.${key}`, {
      defaultValue: jobTypeValue,
    });
  };

  const getTime = (job) => {
    let createdAtDate = job.createdAt || job.created_at;
    let jobCreatedAt = getLastActiveAtTime(createdAtDate, t);

    return jobCreatedAt;
  };

  const getStatusBadge = (employerStatus) => {
    switch (employerStatus) {
      case "Expiring Soon":
        return {
          text: "Expiring soon",
          color: "#F75443",
          icon: <StatusClockIcon />,
        };
      case "Actively Reviewing":
        return {
          text: "Actively reviewing",
          color: "#6AA84F",
          icon: null,
        };
      case "Be an Early Applicant":
        return {
          text: "Be an early applicant",
          color: "#512ACC",
          icon: null,
        };
      default:
        return null;
    }
  };

  const statusBadge = getStatusBadge(job?.employerStatus);

  const renderstatusBadge = () =>
    statusBadge ? (
      <StatusBadgeContainer $color={statusBadge.color}>
        {statusBadge.icon}
        {statusBadge.text}
      </StatusBadgeContainer>
    ) : null;

  function formatSalary(input, crossBorderData) {
    const geoEnv = process.env.NEXT_PUBLIC_JSW_GEOLOCATION;
    let currency;

    // prioritise source_country as it may be jobs utilising "cross border token"
    // if no source_country, it means the job is from the target geolocation of ur api (e.g. my-api OR sg-api)
    // therefore, fallback is to just use geolocation ENV
    if (crossBorderData?.source_country) {
      currency = currencies[crossBorderData?.source_country];
    } else if (geoEnv === "sg") {
      currency = "SGD";
    } else {
      currency = "RM";
    }

    if (!input?.includes(" - ")) {
      const salaryNumber = Number(input?.replace(currency, ""));
      if (salaryNumber < 10000) {
        return `${currency} ${salaryNumber}`;
      }
      const salaryInK = salaryNumber / 1000;
      const startDecimals = salaryInK?.toFixed(1) % 1 === 0 ? 0 : 1;
      return `${currency} ${salaryInK?.toFixed(startDecimals)}K`;
    }
    const [startSalary, endSalary] = input?.split(" - ");

    const startNumber = Number(startSalary?.replace("RM", ""));

    if (startNumber === 0) {
      return `${endSalary}`;
    }

    const endNumber = Number(endSalary?.replace(currency, ""));

    if (startNumber < 10000 && endNumber < 10000) {
      return `${currency} ${startNumber} - ${currency} ${endNumber}`;
    }

    const startSalaryInK = startNumber / 1000;
    const endSalaryInK = endNumber / 1000;

    const startDecimals = startSalaryInK?.toFixed(1) % 1 === 0 ? 0 : 1;
    const endDecimals = endSalaryInK?.toFixed(1) % 1 === 0 ? 0 : 1;

    return `${currency} ${startSalaryInK?.toFixed(
      startDecimals
    )}K - ${currency} ${endSalaryInK?.toFixed(endDecimals)}K`;
  }

  return (
    <CardStyled>
      <BoxStyled flexDirection={"column"} gap={"0.5rem"}>
        <IconTextContainer aria-label="job-salary">
          <CurrencyIconStyled />
          <LabelStyled aria-label="job-salary-value" variant="body1">
            {job?.salary === "Undisclosed" || job?.salary === "Undefined"
              ? t("jobs.salary_undisclosed")
              : formatSalary(job?.salary, job?.crossBorderData)}
          </LabelStyled>
        </IconTextContainer>

        <IconTextContainer aria-label="job-location">
          <MapPinStyled />
          <LabelStyled
            aria-label="job-location-value"
            variant="body1"
            flexWrap={"wrap"}
            textOverflow={"ellipsis"}
            overflow={"hidden"}
            whiteSpace={"nowrap"}
          >
            {locationFilter(job)}
          </LabelStyled>
        </IconTextContainer>
      </BoxStyled>

      <IconTextContainer>
        <DivStyled
          container
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          {job.jobType || job.job_type ? (
            <Link
              href={handleHref("jobType", job)}
              style={{ textDecoration: "none" }}
            >
              <DivStyled
                style={{
                  background: Color.lightBlue,
                  padding: "0 0.5rem",
                  borderRadius: "0.25rem",
                  color: Color.blue,
                  cursor: "pointer",
                }}
              >
                <LabelStyled
                  variant="body1"
                  fontWeight={"bold"}
                  fontSize={"14px"}
                  aria-label="job-type"
                  sx={{
                    color: Color.blue,
                  }}
                >
                  {job?.jobType
                    ? getJobTypeLabel(job?.jobType)
                    : job?.job_type
                    ? getJobTypeLabel(job?.job_type)
                    : null}
                </LabelStyled>
              </DivStyled>
            </Link>
          ) : null}
          {statusBadge && renderstatusBadge()}
        </DivStyled>
      </IconTextContainer>
    </CardStyled>
  );
}

export default SingleJobHighlight;
